/**
 * Smooth Loading State Hook
 * Prevents flickering by debouncing loading state changes
 * Only shows loading indicator if operation takes longer than threshold
 */

import { useState, useEffect, useRef, useCallback } from 'react';

export function useSmoothLoading(options = {}) {
  const {
    // Delay before showing loading indicator (prevents flash for fast requests)
    showDelay = 300,
    // Minimum time to show loading indicator (prevents flash off)
    minDuration = 500,
    // Debounce time for rapid state changes
    debounceTime = 100
  } = options;

  const [isLoading, setIsLoading] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  
  const loadingStartTime = useRef(null);
  const showTimeoutRef = useRef(null);
  const hideTimeoutRef = useRef(null);
  const debounceTimeoutRef = useRef(null);

  // Clear all timeouts
  const clearTimeouts = useCallback(() => {
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
  }, []);

  // Start loading
  const startLoading = useCallback(() => {
    clearTimeouts();
    
    // Debounce rapid start/stop cycles
    debounceTimeoutRef.current = setTimeout(() => {
      setIsLoading(true);
      loadingStartTime.current = Date.now();

      // Delay showing loading indicator
      showTimeoutRef.current = setTimeout(() => {
        if (isLoading) {
          setShowLoading(true);
        }
      }, showDelay);
    }, debounceTime);
  }, [isLoading, showDelay, debounceTime, clearTimeouts]);

  // Stop loading
  const stopLoading = useCallback(() => {
    clearTimeouts();
    
    setIsLoading(false);

    // If loading indicator is shown, keep it for minimum duration
    if (showLoading && loadingStartTime.current) {
      const elapsed = Date.now() - loadingStartTime.current;
      const remaining = minDuration - elapsed;

      if (remaining > 0) {
        hideTimeoutRef.current = setTimeout(() => {
          setShowLoading(false);
          loadingStartTime.current = null;
        }, remaining);
      } else {
        setShowLoading(false);
        loadingStartTime.current = null;
      }
    } else {
      setShowLoading(false);
      loadingStartTime.current = null;
    }
  }, [showLoading, minDuration, clearTimeouts]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTimeouts();
    };
  }, [clearTimeouts]);

  return {
    // Actual loading state
    isLoading,
    // Visual loading state (debounced)
    showLoading,
    // Control functions
    startLoading,
    stopLoading,
    // Helper for wrapping async functions
    withLoading: async (asyncFn) => {
      try {
        startLoading();
        const result = await asyncFn();
        return result;
      } finally {
        stopLoading();
      }
    }
  };
}

/**
 * Hook for silent background updates
 * Never shows loading indicator, perfect for auto-refresh
 */
export function useSilentLoading() {
  const [isLoading, setIsLoading] = useState(false);
  const abortControllerRef = useRef(null);

  const startLoading = useCallback(() => {
    setIsLoading(true);
    abortControllerRef.current = new AbortController();
  }, []);

  const stopLoading = useCallback(() => {
    setIsLoading(false);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  const withSilentLoading = useCallback(async (asyncFn) => {
    if (isLoading) {
      console.log('[SilentLoading] Skipping - already loading');
      return;
    }

    try {
      startLoading();
      const result = await asyncFn();
      return result;
    } catch (error) {
      if (error.name !== 'AbortError') {
        throw error;
      }
    } finally {
      stopLoading();
    }
  }, [isLoading, startLoading, stopLoading]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    isLoading,
    withSilentLoading,
    abort: () => abortControllerRef.current?.abort()
  };
}

export default useSmoothLoading;
